/**
 * useSelectionCapture — capture the current Word selection on demand.
 *
 * Reads selection text via ``readSelectionText`` from wordProbe ONLY on
 * explicit user action (input focus or programmatic call) — never on
 * ambient selection-change events. This preserves the 2026-05-11 design
 * decision that avoids ambient Word.run calls on long CSR drafts.
 *
 * ``syncFromProbe`` is the one ambient input: the selection-chip probe
 * (wordProbe.subscribeSelectionChip) already reads a bounded selection's
 * text, so the shell forwards it here and the pill shows the highlighted
 * text as soon as the writer selects — no extra Word read.
 *
 * SRP: owns only the capture → hold → dismiss lifecycle. No UI, no
 *      document modification, no side effects beyond the single read.
 */

import { useCallback, useRef, useState } from "react";
import { readSelectionText } from "../../services/wordProbe";

export interface CapturedSelection {
  /** Trimmed selection text (always non-empty when set). */
  text: string;
  /** Where the value came from. ``probe`` values track the live Word
   *  selection and clear when it goes away; ``focus`` values (an explicit
   *  read) persist until dismissed. */
  source?: "focus" | "probe";
}

export interface UseSelectionCaptureResult {
  /** The last captured selection, or null when nothing is held. */
  captured: CapturedSelection | null;
  /**
   * Read the current Word selection and store it when non-empty.
   * Replaces any previously captured value.
   * No-ops silently if Word/Office.js is unavailable.
   */
  captureNow: () => Promise<void>;
  /** Discard the captured selection (e.g. after the message is sent). */
  dismiss: () => void;
  /**
   * Mirror the selection-chip probe. Non-empty text replaces the held
   * value unless the writer dismissed that same text; null clears a
   * probe-sourced value (an explicit focus capture is left alone).
   */
  syncFromProbe: (text: string | null) => void;
}

export function useSelectionCapture(): UseSelectionCaptureResult {
  const [captured, setCaptured] = useState<CapturedSelection | null>(null);
  // Text the writer dismissed while it was still selected — the probe
  // must not re-show it until the selection changes.
  const dismissedProbeTextRef = useRef<string | null>(null);
  // Mirror of ``captured`` so ``dismiss`` can read it without a side effect
  // inside a state updater (updaters may run twice under StrictMode).
  const capturedRef = useRef<CapturedSelection | null>(null);
  capturedRef.current = captured;

  const captureNow = useCallback(async () => {
    try {
      const { text } = await readSelectionText();
      if (text?.trim()) {
        const trimmed = text.trim();
        setCaptured((prev) => (prev?.text === trimmed ? prev : { text: trimmed, source: "focus" }));
      }
      // No selection → leave any previously captured value in place so
      // the pill stays visible if the writer had text selected when they
      // first focused but then moved the cursor without re-selecting.
    } catch {
      // Office.js unavailable or Word.run failed — ignore silently.
    }
  }, []);

  const dismiss = useCallback(() => {
    if (capturedRef.current) dismissedProbeTextRef.current = capturedRef.current.text;
    setCaptured(null);
  }, []);

  const syncFromProbe = useCallback((text: string | null) => {
    const trimmed = text?.trim() ?? "";
    if (!trimmed) {
      dismissedProbeTextRef.current = null;
      setCaptured((prev) => (prev?.source === "probe" ? null : prev));
      return;
    }
    if (dismissedProbeTextRef.current === trimmed) return;
    dismissedProbeTextRef.current = null;
    setCaptured((prev) => (prev?.text === trimmed ? prev : { text: trimmed, source: "probe" }));
  }, []);

  return { captured, captureNow, dismiss, syncFromProbe };
}
